/**
 * Card for an activity.
 */
import * as React from 'react';
import { Tag, Zap } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { getActivityTypeName, getActivityStatusConfig } from './activity-table-utils';
import type { Activity } from '@/types/activity.types';

/** Props for {@link ActivityCard}. */
interface ActivityCardProps {
  activity: Activity;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean };
}

/** Card for an activity. */
export const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  onView,
  onEdit,
  onDelete,
  pageActions,
}) => {
  const canEdit = pageActions?.canEditRow ?? true;
  const canDelete = pageActions?.canDeleteRow ?? true;
  const statusConfig = getActivityStatusConfig(activity);
  const StatusIcon = statusConfig.icon;
  const activityTypeName = getActivityTypeName(activity);

  return (
    <TableCard onClick={() => onView?.(activity.id)}>
      <div className="data-card-content">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Tag className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-foreground text-sm">{activity.label}</span>
            <span className="text-xs text-muted-foreground">{activity.code}</span>
          </div>
        </div>

        {activityTypeName ? (
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
            <span className="text-sm text-foreground">{activityTypeName}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
            <span className="text-sm text-muted-foreground">Type non défini</span>
          </div>
        )}

        <div className="mt-2">
          <LabelBadge
            label={statusConfig.label}
            icon={StatusIcon}
            variant={statusConfig.badgeVariant}
            color={statusConfig.badgeColor}
            size="sm"
          />
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(activity.id);
            }}
          />
          {canEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(activity.id);
              }}
            />
          )}
          {canDelete && (
            <Button
              preset="row-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(activity.id);
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
